import React, { useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useParams } from 'react-router-dom';
import { Ticket, X, Info, ChevronRight } from 'lucide-react'; // 引入圖標
import { getUserCoupons } from '../../services/api';

const CouponList = () => {
  const { merchantSlug } = useParams();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // 🌟 新增：控制目前選中的優惠券（用於放大顯示）
  const [selectedCoupon, setSelectedCoupon] = useState(null);

  useEffect(() => {
    const fetchCoupons = async () => {
      try {
        const response = await getUserCoupons(merchantSlug);
        setCoupons(response.data);
      } catch (err) {
        console.error("載入優惠券失敗", err);
      } finally {
        setLoading(false);
      }
    };
    if (merchantSlug) fetchCoupons();
  }, [merchantSlug]);

  if (loading) return <div className="p-10 text-center text-gray-500 font-bold">載入中...</div>;

  return (
    <div className="max-w-md mx-auto p-4 pb-24 bg-gray-50 min-h-screen relative">
      <div className="mb-6">
        <h1 className="text-2xl font-black text-gray-800">我的優惠券</h1>
        <p className="text-gray-500 text-sm">點擊優惠券以出示 QR Code</p>
      </div>

      {coupons.length === 0 ? (
        <div className="bg-white rounded-3xl p-10 text-center shadow-sm border border-gray-100">
          <Ticket className="mx-auto text-gray-200 mb-4" size={48} />
          <p className="text-gray-400 font-medium">目前沒有可用的優惠券</p>
        </div>
      ) : (
        <div className="space-y-4">
          {coupons.map((coupon) => (
            <div 
              key={coupon.id} 
              onClick={() => setSelectedCoupon(coupon)} // 🌟 點擊展開
              className="bg-white rounded-2xl shadow-sm border border-gray-100 flex items-center p-4 active:scale-95 transition-transform cursor-pointer"
            >
              {/* 左側：紅色的裝飾圖示 */}
              <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center text-[#c66dc7]">
                <Ticket size={24} />
              </div>
              
              {/* 中間：文字資訊 */}
              <div className="flex-1 ml-4">
                <h3 className="font-bold text-gray-800">{coupon.template_name}</h3>
                <div className="flex items-center gap-2">
                  <span className="text-[#c66dc7] font-black text-xl">${parseInt(coupon.value)}</span>
                  <span className="text-[10px] text-gray-400">有效至 {new Date(coupon.expires_at).toLocaleDateString()}</span>
                </div>
              </div>

              {/* 右側：箭頭提示 */}
              <ChevronRight size={20} className="text-gray-300" />
            </div>
          ))}
        </div>
      )}

      {/* 🌟 詳情彈窗 (Overlay) 🌟 */}
      {selectedCoupon && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          {/* 背景遮罩 */}
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setSelectedCoupon(null)}
          ></div>

          {/* 票券放大內容卡片 */}
          <div className="relative w-full max-w-sm bg-white rounded-[2rem] shadow-2xl overflow-hidden flex flex-col max-h-[90vh] animate-in fade-in zoom-in duration-300">
            
            {/* 1. 固定頂部裝飾條 */}
            <div className="h-3 bg-[#c66dc7] w-full shrink-0"></div>
            
            {/* 2. 關閉按鈕 */}
            <button 
              onClick={() => setSelectedCoupon(null)}
              className="absolute top-6 right-6 p-2 bg-gray-100 rounded-full text-gray-500 hover:bg-gray-200 z-10"
            >
              <X size={20} />
            </button>

            {/* 3. 可捲動內容區 */}
            <div className="overflow-y-auto p-8 pt-10">
              <div className="text-center mb-6">
                <span className="px-3 py-1 bg-red-50 text-[#c66dc7] text-xs font-bold rounded-full uppercase">
                  專屬優惠券
                </span>
                <h2 className="text-2xl font-black text-gray-800 mt-2">{selectedCoupon.template_name}</h2>
                <div className="text-4xl font-black text-[#c66dc7] mt-2">${parseInt(selectedCoupon.value)} 折價劵</div>
              </div>

              {/* QR Code 區塊 */}
              <div className="flex flex-col items-center justify-center p-6 bg-gray-50 rounded-3xl border-2 border-dashed border-gray-200 mb-6">
                <QRCodeSVG value={selectedCoupon.coupon_code} size={180} level="H" />
                <p className="mt-4 font-mono text-gray-400 tracking-widest">{selectedCoupon.coupon_code}</p>
                <div className="mt-2 text-xs text-blue-600 font-bold flex items-center gap-1">
                  <Info size={12} /> 請向店員出示此碼掃描
                </div>
              </div>

              {/* 注意事項與條款 */}
              <div className="space-y-4 text-sm">
                <div>
                  <h4 className="font-bold text-gray-700 flex items-center gap-1">
                    <div className="w-1 h-4 bg-[#c66dc7] rounded-full"></div>
                    使用說明
                  </h4>
                  <ul className="text-gray-500 mt-2 list-disc pl-5 space-y-2">
                    <li>結帳前請主動出示此 QR Code。</li>
                    <li>本券僅限於 KaKa卡卡兒童超跑 現場使用。</li>
                    <li>每筆消費限用一張優惠券。</li>
                    <li>請確認您的手機螢幕亮度已調高，以便掃描。</li>
                  </ul>
                </div>

                <div className="pt-4 border-t border-gray-100 pb-4">
                  <h4 className="font-bold text-gray-700">條款與細則</h4>
                  <p className="text-[11px] text-gray-400 mt-1 leading-relaxed">
                    1. 本券不得兌換現金或找零。<br/>
                    2. 逾期無效，恕不補發。<br/>
                    3. 本公司保留修改活動之權利。<br/>
                    4. 如有爭議，KaKaTW 保留最終決定權。
                  </p>
                </div>
              </div>

              {/* 回復原本大小的按鈕 */}
              <button 
                onClick={() => setSelectedCoupon(null)}
                className="w-full mt-4 py-4 bg-gray-100 text-gray-600 rounded-2xl text-sm font-bold active:bg-gray-200 transition-colors"
              >
                返回列表
              </button>
            </div>

            {/* 票券裝飾打孔 (視覺效果) */}
            <div className="absolute top-1/2 -left-4 w-8 h-8 bg-black/10 rounded-full shrink-0"></div>
            <div className="absolute top-1/2 -right-4 w-8 h-8 bg-black/10 rounded-full shrink-0"></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CouponList;